import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Snackbar,
    Stack,
    Typography
} from '@mui/material';
import useBoolean from 'hooks/useBoolean';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import checkHotelInvoice from 'utils/checkHotelInvoice';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import FormatDate from 'utils/formatDate';

const HotelPayment = () => {
    const navigate = useNavigate();

    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [invoice, setInvoice] = useState(null);
    
    const openSnackBar = useBoolean();
    const isPaymentProcess = useBoolean();
    const openModalPayment = useBoolean();
    const openModalCancel = useBoolean();

    useEffect(() => {
        const verifyPendingInvoice = async () => {
            const { pendingHotelInvoice } = await checkHotelInvoice();
            if (!pendingHotelInvoice) {
                navigate('/dashboard/hotel-reservation');
            }

            setInvoice(pendingHotelInvoice);
        };
        verifyPendingInvoice();
    }, [navigate]);

    const handleCreateInvoice = async () => {
        isPaymentProcess.onTrue();
        try {
            if (invoice?.invoiceUrl) {
                openModalPayment.onTrue();
            } else {
                // Fetch latest invoice for the reservation if needed
                // Fallback: navigate to payment complete
                navigate('/dashboard/payment-complete', {
                    state: { type: 'hotel' },
                });
            }
        } catch (err) {
            setSnackbarMessage(err.message || 'Payment processing failed.');
            openSnackBar.onTrue();
        } finally {
            isPaymentProcess.onFalse();
        }
    };

    const handleCancelInvoice = async () => {
        try {
            isPaymentProcess.onTrue();
            await axiosInstance.patch(
                `/api/prv/member-invoices/${invoice.id}`,
                { status: 'cancel' },
            );
            openModalCancel.onFalse();
            navigate('/dashboard/hotel-reservation');
        } catch (err) {
            setSnackbarMessage(err.message || 'Failed to cancel invoice.');
            openSnackBar.onTrue();
        } finally {
            isPaymentProcess.onFalse();
        }
    };

    const handleDownloadInvoice = async () => {
        try {
            await downloadFile(
                `/api/prv/member-invoices/download/${invoice.id}`,
            );
        } catch (err) {
            setSnackbarMessage('Failed to download invoice.');
            openSnackBar.onTrue();
        }
    };

    return (
        <DashboardLayout>
            <Box pt={2} pb={4}>
                <Button
                    startIcon={<ArrowBackIosNewOutlinedIcon />}
                    onClick={() => navigate('/dashboard/hotel-reservation')}
                >
                    Back
                </Button>
                <Grid container spacing={3} mt={3}>
                    <Grid item md={7} xs={12}>
                        <Card sx={{ p: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Hotel Reservation Summary
                            </Typography>
                            <Stack spacing={1}>
                                <Typography>Room Type: {invoice?.reservation?.roomType.name}</Typography>
                                <Typography>
                                    Check-in:{' '}
                                    {FormatDate(invoice?.reservation?.checkIn?._date, 'dd MMMM yyyy')}
                                </Typography>
                                <Typography>
                                    Check-out:{' '}
                                    {FormatDate(invoice?.reservation?.checkOut?._date, 'dd MMMM yyyy')}
                                </Typography>
                                <Typography>Nights: {invoice?.reservation?.nights}</Typography>
                                <Typography>
                                    Price per Night:{' '}
                                    {formatCurrency(invoice?.reservation?.roomType?.price || 0)}
                                </Typography>
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" color="error.main">
                                Total: {formatCurrency(invoice?.totalPrice || 0)}
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item md={5} xs={12}>
                        <Card sx={{ p: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Proceed to Payment
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <LoadingButton
                                variant="contained"
                                fullWidth
                                loading={isPaymentProcess.value}
                                onClick={handleCreateInvoice}
                            >
                                {isPaymentProcess.value
                                    ? 'Processing...'
                                    : 'Pay Now'}
                            </LoadingButton>
                            <Stack spacing={1} direction="row" sx={{ mt: 2, width: '100%' }}>
                                <Button
                                    variant="outlined"
                                    sx={{ flex: 1 }}
                                    onClick={handleDownloadInvoice}
                                >
                                    Download Invoice
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{ flex: 1 }}
                                    onClick={openModalCancel.onTrue}
                                >
                                    Cancel Invoice
                                </Button>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>

                <Dialog
                    fullScreen
                    open={openModalPayment.value}
                    onClose={openModalPayment.onFalse}
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        Online Payment
                        <Button
                            onClick={openModalPayment.onFalse}
                            variant="outlined"
                            startIcon={<ArrowBackIosNewOutlinedIcon />}
                        >
                            Back
                        </Button>
                    </DialogTitle>
                    <DialogContent>
                        {invoice?.invoiceUrl && (
                            <iframe
                                src={invoice.invoiceUrl}
                                style={{ border: 0, height: '100vh' }}
                                width="100%"
                                title="hotel-invoice"
                            ></iframe>
                        )}
                    </DialogContent>
                </Dialog>

                <Dialog
                    open={openModalCancel.value}
                    onClose={openModalCancel.onFalse}
                >
                    <DialogTitle>Cancel Hotel Invoice</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to cancel this invoice?
                        </Typography>
                    </DialogContent>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ p: 2, justifyContent: 'flex-end' }}
                    >
                        <Button onClick={openModalCancel.onFalse}>No</Button>
                        <LoadingButton
                            loading={isPaymentProcess.value}
                            color="error"
                            variant="contained"
                            onClick={handleCancelInvoice}
                        >
                            {isPaymentProcess.value
                                ? 'Cancelling...'
                                : 'Yes, Cancel'}
                        </LoadingButton>
                    </Stack>
                </Dialog>

                <Snackbar
                    open={openSnackBar.value}
                    autoHideDuration={3000}
                    onClose={openSnackBar.onFalse}
                >
                    <Alert severity="error">{snackbarMessage}</Alert>
                </Snackbar>
            </Box>
        </DashboardLayout>
    );
};

export default HotelPayment;
